import { z } from "zod/v4";

import { createWorkspaceSchemas } from "../../agentic-codegen";

/**
 * The fixed entry module every recommendation-rule workspace must contain. It
 * holds the canonical `export async function evaluate({ request, context })`
 * and is the module the bundler/test-runner build from.
 */
export const AGENTIC_LOGIC_RULE_ENTRY_FILENAME =
  "recommendation-rule.ts" as const;

/**
 * Environment-injected modules that resolve at editor/bundle/test time but are
 * NEVER persisted in a workspace's `files` map:
 *  - `types.ts`  — the readonly contract-types module (shown as a locked tab).
 *  - `sdk.ts`    — the ambient SDK binding (`sdk`, `attempt`, `firstNonEmpty`,
 *                  `fillTo`; no tab, imports auto-injected).
 *  - `logger.ts` — the hidden runtime logger (no tab, import auto-injected).
 *  - `determinism.ts` — the determinism shadows (`Date`, `Math`, `crypto`, …)
 *                  that the auto-injected per-file prologue imports, so the
 *                  ban binds rule modules lexically instead of freezing the
 *                  real globals for the whole process.
 *  - `test-harness.ts` — the suite-only fake-SDK installer
 *                  (`installAgenticLogicTestSdkRuntime`), injected ONLY into
 *                  test-suite runs — never into published or sandbox bundles,
 *                  so shipped rules cannot install a fake SDK.
 * All are rejected as user-authored filenames so a saved workspace can never
 * shadow them.
 */
export const AGENTIC_LOGIC_TYPES_FILENAME = "types.ts";
export const AGENTIC_LOGIC_SDK_FILENAME = "sdk.ts";
export const AGENTIC_LOGIC_LOGGER_FILENAME = "logger.ts";
export const AGENTIC_LOGIC_DETERMINISM_FILENAME = "determinism.ts";
export const AGENTIC_LOGIC_TEST_HARNESS_FILENAME = "test-harness.ts";
export const AGENTIC_LOGIC_RESERVED_FILENAMES = [
  AGENTIC_LOGIC_TYPES_FILENAME,
  AGENTIC_LOGIC_SDK_FILENAME,
  AGENTIC_LOGIC_LOGGER_FILENAME,
  AGENTIC_LOGIC_DETERMINISM_FILENAME,
  AGENTIC_LOGIC_TEST_HARNESS_FILENAME,
] as const;

const schemas = createWorkspaceSchemas({
  entryFilename: AGENTIC_LOGIC_RULE_ENTRY_FILENAME,
  reservedFilenames: AGENTIC_LOGIC_RESERVED_FILENAMES,
});

/**
 * A recommendation-rule workspace: the user-editable files (the fixed entry
 * plus any helper modules) keyed by filename -> content. The reserved modules
 * above are injected by the tooling and never stored here.
 */
export const AgenticLogicRuleWorkspaceSchema = schemas.WorkspaceSchema;
export type AgenticLogicRuleWorkspace = z.infer<
  typeof AgenticLogicRuleWorkspaceSchema
>;

/**
 * Save-path validator: the base workspace shape plus the filename-safety
 * refinement. Kept separate from {@link AgenticLogicRuleWorkspaceSchema} so consumers
 * that only need the base object shape (and `.omit()`/`.extend()`, which
 * zod-v4 disallows on a refined schema) can compose the unrefined schema.
 */
export const AgenticLogicRuleWorkspaceInputSchema =
  schemas.WorkspaceInputSchema;
